import React, { useEffect, useMemo, useRef, useState } from "react";

// Tolerant imports (default or named)
import * as ChatPaneMod from "./ChatPane";
import * as YouTubePaneMod from "./YouTubePane";
import * as BingoBoardMod from "./BingoBoard";
import * as PlaylistManagerMod from "./PlaylistManager";

const Chat: any = (ChatPaneMod as any).default ?? (ChatPaneMod as any).ChatPane;
const YT: any = (YouTubePaneMod as any).default ?? (YouTubePaneMod as any).YouTubePane;
const Board: any = (BingoBoardMod as any).default ?? (BingoBoardMod as any).BingoBoard;
const Playlist: any = (PlaylistManagerMod as any).default ?? (PlaylistManagerMod as any).PlaylistManager;

const HTTP_BASE =
  (import.meta.env.VITE_HTTP_BASE as string) ||
  (typeof window !== "undefined" ? window.location.origin : "http://127.0.0.1:8787");
const WS_BASE =
  (import.meta.env.VITE_WS_BASE as string) ||
  (typeof window !== "undefined" ? window.location.origin.replace(/^http/i, "ws") : "ws://127.0.0.1:8787");

const estimateClockSkew = (serverTimeMs: number) => Date.now() - serverTimeMs;

type VideoSnap = {
  id: string | null;
  paused: boolean;
  positionSec: number;
  startedAtMs: number;
  version: number;
} | null;

type FullState = {
  video: any;
  players: Array<{ id: string; nick: string }>;
  game: {
    type: "bingo";
    seed: string;
    themeSlug: string;
    cards: Record<string, string[]>;
    marks: Record<string, boolean[]>;
    queue: string[];
    queueIndex: number;
  };
};

type ChatMessage = {
  type: string;
  id: string;
  nick: string;
  text: string;
  ts: number;
};

type SystemMessage = {
  type: "SYSTEM";
  event: "JOIN" | "LEAVE";
  id: string;
  nick: string;
};

type WinMessage = {
  type: "BINGO_WIN";
  id: string;
  nick: string;
  pattern: string;
};

export default function App() {
  const [roomName, setRoomName] = useState<string>("default");
  const [nick, setNick] = useState<string>(() => localStorage.getItem("nick") || "guest");
  const [connected, setConnected] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);

  const wsRef = useRef<WebSocket | null>(null);
  const [myId, setMyId] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [state, setState] = useState<FullState | null>(null);
  const [videoSnap, setVideoSnap] = useState<VideoSnap>(null);
  const [serverTimeMs, setServerTimeMs] = useState<number>(0);
  const clockSkewMsRef = useRef<number>(0);

  // Chat messages and notifications
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [notifications, setNotifications] = useState<string[]>([]);

  // Clear reconnect timeout on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Main WebSocket connection effect - only depends on roomName
  useEffect(() => {
    if (connecting) return;
    
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setConnecting(true);
    setConnected(false);
    
    const url = `${WS_BASE}/ws/${encodeURIComponent(roomName)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    const connectTimeout = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.close();
        setConnecting(false);
      }
    }, 10000);

    ws.addEventListener("open", () => {
      clearTimeout(connectTimeout);
      setConnected(true);
      setConnecting(false);
      console.log("WebSocket connected, sending JOIN with nick:", nick);
      localStorage.setItem("nick", nick);
      ws.send(JSON.stringify({ type: "JOIN", nick }));
      ws.send(JSON.stringify({ type: "SYNC?" }));
    });

    ws.addEventListener("message", (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        switch (msg.type) {
          case "YOU_ARE":
            console.log("Received YOU_ARE:", msg.id);
            setMyId(msg.id || null);
            break;
          case "STATE":
            setState(msg.state);
            break;
          case "STATE_VIDEO":
            if (msg.serverTimeMs) {
              setServerTimeMs(msg.serverTimeMs);
              clockSkewMsRef.current = estimateClockSkew(msg.serverTimeMs);
            }
            setVideoSnap(msg.video);
            break;
          case "CHAT":
            setChatMessages(prev => [...prev, msg]);
            break;
          case "SYSTEM":
            const systemMsg = msg as SystemMessage;
            const eventText = systemMsg.event === "JOIN" ? "joined the room" : "left the room";
            setChatMessages(prev => [...prev, {
              type: "SYSTEM",
              id: "system",
              nick: "System",
              text: `${systemMsg.nick} ${eventText}`,
              ts: Date.now()
            }]);
            break;
          case "BINGO_WIN":
            const winMsg = msg as WinMessage;
            const winText = `🎉 ${winMsg.nick} got BINGO! (${winMsg.pattern})`;
            setNotifications(prev => [...prev, winText]);
            setChatMessages(prev => [...prev, {
              type: "SYSTEM",
              id: "system", 
              nick: "System",
              text: winText,
              ts: Date.now()
            }]);
            // Auto-remove notification after 5 seconds
            setTimeout(() => {
              setNotifications(prev => prev.filter(n => n !== winText));
            }, 5000);
            break;
          case "BINGO_MARK":
            // Optional: show mark updates in chat for transparency
            break;
        }
      } catch (e) {
        console.error("Error parsing WS message:", e);
      }
    });

    ws.addEventListener("close", (event) => {
      clearTimeout(connectTimeout);
      setConnected(false);
      setConnecting(false);
      console.log("WebSocket closed:", event.code, event.reason);
      
      // Only auto-reconnect if it wasn't a clean close and we're still on the same room
      if (event.code !== 1000 && wsRef.current === ws) {
        console.log("Scheduling reconnect in 3 seconds...");
        reconnectTimeoutRef.current = setTimeout(() => {
          if (wsRef.current === ws) {
            console.log("Attempting reconnect...");
            // Trigger reconnect by forcing the effect to re-run
            setRoomName(prev => prev + ""); // This forces re-render without changing the value
          }
        }, 3000);
      }
    });

    ws.addEventListener("error", (error) => {
      clearTimeout(connectTimeout);
      console.error("WebSocket error:", error);
      setConnecting(false);
    });

    return () => {
      clearTimeout(connectTimeout);
      ws.close();
      if (wsRef.current === ws) wsRef.current = null;
    };
  }, [roomName]); // Only roomName in dependencies

  // Separate effect for nickname updates on existing connections
  useEffect(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && myId) {
      console.log("Updating nickname on existing connection:", nick);
      ws.send(JSON.stringify({ type: "JOIN", nick }));
    }
  }, [nick, myId]); // Separate effect for nick updates

  // Generic sender with connection check
  const send = (msg: any) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("Cannot send message: WebSocket not connected");
      return false;
    }
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch (e) {
      console.error("Error sending message:", e);
      return false;
    }
  };

  // Authoritative video sender
  const sendVideoCmd = (cmd: "LOAD" | "PLAY" | "PAUSE" | "SEEK", payload?: { id?: string; pos?: number }) => {
    return send({ type: "VIDEO_CMD", cmd, ...payload });
  };

  // Chat sender
  const sendChat = (text: string) => {
    return send({ type: "CHAT", text });
  };

  // Game actions
  const setTheme = (slug: string) => send({ type: "BINGO_SET_THEME", slug });
  const newSeed = () => send({ type: "GAME_NEW_SEED" });
  const resetMarks = () => send({ type: "GAME_RESET" });
  const markCell = (index: number) => send({ type: "BINGO_MARK", index });

  // Provide per-user context some components expect
  const stateForChildren = useMemo(
    () => (state ? { ...state, me: myId } : null),
    [state, myId]
  );

  const myCard = useMemo(() => (myId && state ? state.game.cards?.[myId] || null : null), [myId, state]);
  const myMarks = useMemo(() => (myId && state ? state.game.marks?.[myId] || null : null), [myId, state]);

  const connectionStatus = useMemo(() => {
    if (connecting) return { text: "Connecting...", color: "text-yellow-400", icon: "🔄" };
    if (connected) return { text: "Connected", color: "text-green-400", icon: "🟢" };
    return { text: "Disconnected", color: "text-red-400", icon: "🔴" };
  }, [connected, connecting]);

  const handleRoomChange = (newRoom: string) => {
    if (newRoom !== roomName) {
      setChatMessages([]); // Clear chat when changing rooms
      setNotifications([]);
      setRoomName(newRoom);
    }
  };

  const handleNickChange = (newNick: string) => {
    if (newNick !== nick) {
      setNick(newNick);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950">
      {/* Background effects */}
      <div className="fixed inset-0 bg-gradient-to-tr from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.1),transparent_50%)] pointer-events-none" />
      
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map((notification, i) => (
            <div
              key={i}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-lg animate-pulse-strong"
            >
              {notification}
            </div>
          ))}
        </div>
      )}

      {/* Main Grid - Mobile: stack vertically, Desktop: sidebar layout */}
      <div className="min-h-screen p-4 grid gap-6 
                      grid-cols-1 
                      lg:grid-cols-[1fr_350px]">
        
        {/* Main Content Column */}
        <div className="space-y-6">
          {/* Connection Controls */}
          <div className="glass rounded-2xl p-6 shadow-xl">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="space-y-1">
                  <label className="text-xs text-slate-400 uppercase tracking-wide">Room</label>
                  <input
                    className="bg-slate-800/70 border border-slate-700/50 rounded-xl px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    value={roomName}
                    onChange={(e) => handleRoomChange(e.target.value)}
                    placeholder="Enter room name..."
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-400 uppercase tracking-wide">Nickname</label>
                  <input
                    className="bg-slate-800/70 border border-slate-700/50 rounded-xl px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    value={nick}
                    onChange={(e) => handleNickChange(e.target.value)}
                    placeholder="Your nickname..."
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm">{connectionStatus.icon}</span>
                <span className={`text-sm font-medium ${connectionStatus.color}`}>
                  {connectionStatus.text}
                </span>
              </div>
            </div>
          </div>

          {/* YouTube Player */}
          <div className="glass rounded-2xl p-6 shadow-xl">
            <YT
              snapshot={videoSnap}
              serverTimeMs={serverTimeMs}
              clockSkewMsRef={clockSkewMsRef}
              onVideoCmd={sendVideoCmd}
            />
          </div>

          {/* Mobile Chat (visible only on mobile) - Fixed height container */}
          <div className="lg:hidden">
            <div className="glass rounded-2xl shadow-xl overflow-hidden h-[400px]">
              <Chat items={chatMessages} onSend={sendChat} />
            </div>
          </div>

          {/* Game Controls */}
          <div className="glass rounded-2xl p-6 shadow-xl">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  🎮
                </div>
                Game Controls
              </h3>
              
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setTheme("wrestling")} 
                  className="btn-bounce bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-4 py-2 rounded-xl font-medium transition-all shadow-lg"
                >
                  🤼 Wrestling
                </button>
                <button 
                  onClick={() => setTheme("bodycam")} 
                  className="btn-bounce bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-4 py-2 rounded-xl font-medium transition-all shadow-lg"
                >
                  📹 Bodycam
                </button>
                <button 
                  onClick={() => setTheme("rap-battle")} 
                  className="btn-bounce bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-xl font-medium transition-all shadow-lg"
                >
                  🎤 Rap Battle
                </button>
                <button 
                  onClick={newSeed} 
                  className="btn-bounce bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-4 py-2 rounded-xl font-medium transition-all shadow-lg"
                >
                  🔄 New Round
                </button>
                <button 
                  onClick={resetMarks} 
                  className="btn-bounce bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white px-4 py-2 rounded-xl font-medium transition-all shadow-lg"
                >
                  ↻ Reset Marks
                </button>
              </div>
            </div>

            {/* Bingo Board */}
            <Board 
              myId={myId} 
              card={myCard} 
              marks={myMarks} 
              onMark={markCell} 
              state={stateForChildren} 
              send={send} 
            />
          </div>

          {/* Playlist Manager */}
          <div className="glass rounded-2xl shadow-xl overflow-hidden">
            <Playlist state={stateForChildren} send={send} defaultCollapsed={false} />
          </div>
        </div>

        {/* Sidebar - Chat (Desktop only) - Fixed height container */}
        <aside className="hidden lg:block">
          <div className="sticky top-4">
            <div className="glass rounded-2xl shadow-xl overflow-hidden h-[calc(100vh-2rem)]">
              <Chat items={chatMessages} onSend={sendChat} />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}